import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Lock, Mail, User, Eye, EyeOff, Loader2, CheckCircle, Check, X, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalLogin = () => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'verify', 'forgot', 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);
  const [fallbackCode, setFallbackCode] = useState(''); // Code shown when email fails
  
  // Reset password
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Password validation (used for both signup and reset)
  const currentPasswordToValidate = mode === 'reset' ? newPassword : password;
  const passwordChecks = {
    length: currentPasswordToValidate.length >= 8,
    uppercase: /[A-Z]/.test(currentPasswordToValidate),
    lowercase: /[a-z]/.test(currentPasswordToValidate),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(currentPasswordToValidate)
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      if (mode === 'signup') {
        // Validate password
        if (!isPasswordValid) {
          setError('Please meet all password requirements');
          setIsSubmitting(false);
          return;
        }
        
        // Sign up - send verification code
        const response = await axios.post(`${API_URL}/api/portal/auth/signup`, {
          name,
          email,
          password
        });
        
        // Check if email failed and we got a fallback code
        if (response.data.email_status === 'failed' && response.data.verification_code) {
          setFallbackCode(response.data.verification_code);
        }
        
        // Move to verification screen
        setMode('verify');
        startResendTimer();
      } else {
        // Login
        const response = await axios.post(
          `${API_URL}/api/portal/auth/login`,
          { email, password },
          { withCredentials: true }
        );
        
        const user = response.data;
        
        // Redirect based on role
        if (user.role === 'investor') {
          navigate('/portal/investor', { state: { user }, replace: true });
        } else if (user.role === 'company') {
          navigate('/portal/company', { state: { user }, replace: true });
        } else {
          navigate('/portal', { state: { user }, replace: true });
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/portal/auth/verify`, {
        email,
        code: verificationCode
      });
      
      setSuccess(response.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    
    try {
      await axios.post(`${API_URL}/api/portal/auth/resend-code`, { email });
      startResendTimer();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    }
  };

  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/portal/auth/forgot-password`, { email });
      setMode('reset');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode || resetCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/portal/auth/reset-password`, {
        email,
        code: resetCode,
        new_password: newPassword
      });
      setSuccess('Your password has been reset successfully. You can now log in with your new password.');
      setResetCode('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resend reset code
  const handleResendResetCode = async () => {
    if (resendTimer > 0) return;
    
    try {
      await axios.post(`${API_URL}/api/portal/auth/forgot-password`, { email });
      startResendTimer();
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    }
  };

  // Success screen after verification or password reset
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="font-heading text-3xl font-bold text-white tracking-tight">
              HÜBNER<span className="text-[#60B5AF]">.</span>
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-2">
              Account Created!
            </h2>
            <p className="font-body text-[#1A3A4A]/70 mb-6">
              {success}
            </p>
            <Button
              onClick={() => { setSuccess(''); setMode('login'); setPassword(''); }}
              className="w-full bg-[#1A3A4A] text-white hover:bg-[#1A3A4A]/90 rounded-xl h-12"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Verification code screen
  if (mode === 'verify') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="font-heading text-3xl font-bold text-white tracking-tight">
              HÜBNER<span className="text-[#60B5AF]">.</span>
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <h1 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-2">
                Enter verification code
              </h1>
              <p className="font-body text-[#1A3A4A]/60 text-sm">
                We just sent you a code. Please check your inbox.
              </p>
            </div>

            {/* Email display */}
            <div className="flex items-center gap-3 p-4 bg-[#F8FAFB] rounded-xl mb-6">
              <Mail className="w-5 h-5 text-[#1A3A4A]/40" />
              <span className="font-body text-[#1A3A4A]">{email}</span>
            </div>

            {/* Fallback code display when email fails */}
            {fallbackCode && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
                <p className="font-body text-sm text-amber-800 mb-2">
                  <strong>Email delivery unavailable.</strong> Use this code:
                </p>
                <div className="flex items-center justify-center gap-2 p-3 bg-white rounded-lg">
                  <span className="font-mono text-2xl font-bold tracking-widest text-[#1A3A4A]">
                    {fallbackCode}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleVerifyCode} className="space-y-4">
              {/* Verification code label with resend */}
              <div className="flex items-center justify-between">
                <Label className="font-body text-sm font-medium text-[#1A3A4A]">
                  Verification code
                </Label>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendTimer > 0}
                  className={`font-body text-sm ${
                    resendTimer > 0 
                      ? 'text-[#1A3A4A]/40 cursor-not-allowed' 
                      : 'text-[#3B5BDB] hover:underline'
                  }`}
                >
                  {resendTimer > 0 ? `Resend (${resendTimer})` : 'Resend'}
                </button>
              </div>

              {/* Code input */}
              <Input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter verification code"
                required
                maxLength={6}
                className="h-12 bg-[#F8FAFB] border-0 rounded-xl font-body text-center text-lg tracking-widest"
              />

              {/* Error message */}
              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-xl font-body text-sm">
                  {error}
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                disabled={isSubmitting || verificationCode.length !== 6}
                className="w-full bg-[#3B5BDB] text-white hover:bg-[#3B5BDB]/90 rounded-xl h-12 font-body font-medium"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>

            {/* Back to signup */}
            <p className="text-sm text-[#1A3A4A]/60 font-body mt-6 text-center">
              <button
                type="button"
                onClick={() => { setMode('signup'); setVerificationCode(''); setError(''); }}
                className="text-[#3B5BDB] hover:underline"
              >
                ← Back to sign up
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Forgot Password screen
  if (mode === 'forgot') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="font-heading text-3xl font-bold text-white tracking-tight">
              HÜBNER<span className="text-[#60B5AF]">.</span>
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#3B5BDB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-7 h-7 text-[#3B5BDB]" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-2">
                Reset your password
              </h2>
              <p className="font-body text-[#1A3A4A]/70 text-sm">
                Enter your email and we'll send you a reset code
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#1A3A4A]/80">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3A4A]/40" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="h-12 bg-[#F8FAFB] border-0 rounded-xl pl-10 font-body"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-xl font-body text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || !email}
                className="w-full bg-[#3B5BDB] text-white hover:bg-[#3B5BDB]/90 rounded-xl h-12 font-body font-medium"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</>
                ) : (
                  'Send Reset Code'
                )}
              </Button>
            </form>

            <p className="text-sm text-[#1A3A4A]/60 font-body mt-6 text-center">
              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); }}
                className="text-[#3B5BDB] hover:underline flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Reset Password screen (enter code + new password)
  if (mode === 'reset') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="font-heading text-3xl font-bold text-white tracking-tight">
              HÜBNER<span className="text-[#60B5AF]">.</span>
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#3B5BDB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-[#3B5BDB]" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-2">
                Enter new password
              </h2>
              <p className="font-body text-[#1A3A4A]/70 text-sm">
                We sent a code to <strong>{email}</strong>
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Reset Code */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="font-body text-sm text-[#1A3A4A]/80">Reset code</Label>
                  <button
                    type="button"
                    onClick={handleResendResetCode}
                    disabled={resendTimer > 0}
                    className={`font-body text-sm ${
                      resendTimer > 0 
                        ? 'text-[#1A3A4A]/40 cursor-not-allowed' 
                        : 'text-[#3B5BDB] hover:underline'
                    }`}
                  >
                    {resendTimer > 0 ? `Resend (${resendTimer})` : 'Resend'}
                  </button>
                </div>
                <Input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  className="h-12 bg-[#F8FAFB] border-0 rounded-xl font-body text-center text-lg tracking-widest"
                />
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#1A3A4A]/80">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3A4A]/40" />
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Create a strong password"
                    required
                    className="h-12 bg-[#F8FAFB] border-0 rounded-xl pl-10 pr-10 font-body"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A3A4A]/40 hover:text-[#1A3A4A]/60"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password requirements */}
                {newPassword && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <PasswordCheck passed={passwordChecks.length} label="8+ characters" />
                    <PasswordCheck passed={passwordChecks.uppercase} label="Uppercase" />
                    <PasswordCheck passed={passwordChecks.lowercase} label="Lowercase" />
                    <PasswordCheck passed={passwordChecks.special} label="Special char" />
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-xl font-body text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || resetCode.length !== 6 || !isPasswordValid}
                className="w-full bg-[#3B5BDB] text-white hover:bg-[#3B5BDB]/90 rounded-xl h-12 font-body font-medium"
              >
                {isSubmitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Resetting...</>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>

            <p className="text-sm text-[#1A3A4A]/60 font-body mt-6 text-center">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setResetCode(''); setNewPassword(''); setError(''); }}
                className="text-[#3B5BDB] hover:underline flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Use different email
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Login/Signup screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A3A4A] to-[#0f252e] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="font-heading text-3xl font-bold text-white tracking-tight">
            HÜBNER<span className="text-[#60B5AF]">.</span>
          </a>
          <p className="font-body text-white/60 mt-2">Client Portal</p>
        </div>

        {/* Login/Signup Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="font-heading text-2xl font-semibold text-[#1A3A4A] mb-1">
              {mode === 'signup' ? 'Sign up' : 'Log in'}
            </h1>
            <p className="font-body text-[#1A3A4A]/60 text-sm">
              {mode === 'signup' ? 'Join us today!' : 'Welcome back!'}
            </p>
          </div>

          {/* Google Login Button */}
          <Button
            onClick={login}
            disabled={loading}
            className="w-full bg-white border-2 border-[#1A3A4A]/10 text-[#1A3A4A] hover:bg-[#F8FAFB] hover:border-[#1A3A4A]/20 rounded-xl h-12 font-body font-medium flex items-center justify-center gap-3 transition-all mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 border-t border-[#1A3A4A]/10"></div>
            <span className="text-xs text-[#1A3A4A]/40 font-body">OR</span>
            <div className="flex-1 border-t border-[#1A3A4A]/10"></div>
          </div>

          {/* Email Form */}
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            {/* Name field - only for signup */}
            {mode === 'signup' && (
              <div className="space-y-2">
                <Label className="font-body text-sm font-medium text-[#1A3A4A]">
                  Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3A4A]/30" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required={mode === 'signup'}
                    className="pl-10 h-12 bg-[#F8FAFB] border-0 rounded-xl font-body"
                  />
                </div>
              </div>
            )}

            {/* Email field */}
            <div className="space-y-2">
              <Label className="font-body text-sm font-medium text-[#1A3A4A]">
                Email <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3A4A]/30" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="pl-10 h-12 bg-[#F8FAFB] border-0 rounded-xl font-body"
                />
              </div>
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <Label className="font-body text-sm font-medium text-[#1A3A4A]">
                Password <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1A3A4A]/30" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'Create a password' : 'Enter your password'}
                  required
                  className="pl-10 pr-10 h-12 bg-[#F8FAFB] border-0 rounded-xl font-body"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A3A4A]/30 hover:text-[#1A3A4A]/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password requirements - only for signup */}
              {mode === 'signup' && (
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-[#1A3A4A]/50 font-body mb-2">Password must contain:</p>
                  <PasswordCheck passed={passwordChecks.length} label="At least 8 characters" />
                  <PasswordCheck passed={passwordChecks.uppercase} label="One uppercase letter" />
                  <PasswordCheck passed={passwordChecks.lowercase} label="One lowercase letter" />
                  <PasswordCheck passed={passwordChecks.special} label="One special character (!@#$%...)" />
                </div>
              )}

              {/* Forgot password link - only for login */}
              {mode === 'login' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setMode('forgot'); setError(''); }}
                    className="text-sm text-[#3B5BDB] hover:underline font-body"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-xl font-body text-sm">
                {error}
              </div>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              disabled={isSubmitting || (mode === 'signup' && !isPasswordValid)}
              className="w-full bg-[#3B5BDB] text-white hover:bg-[#3B5BDB]/90 rounded-xl h-12 font-body font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Please wait...</>
              ) : (
                'Continue'
              )}
            </Button>
          </form>

          {/* Terms */}
          {mode === 'signup' && (
            <p className="text-xs text-[#1A3A4A]/50 font-body mt-4 text-center">
              By signing up, you agree to our{' '}
              <a href="#" className="text-[#3B5BDB] hover:underline">Terms and Conditions</a>
              {' & '}
              <a href="#" className="text-[#3B5BDB] hover:underline">Privacy Policy</a>
            </p>
          )}

          {/* Switch mode */}
          <p className="text-sm text-[#1A3A4A]/60 font-body mt-6 text-center">
            {mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-[#3B5BDB] hover:underline font-medium"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setError(''); }}
                  className="text-[#3B5BDB] hover:underline font-medium"
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <a href="/" className="font-body text-white/40 text-sm hover:text-white/60">
            ← Back to website
          </a>
        </div>
      </div>
    </div>
  );
};

// Password check component
const PasswordCheck = ({ passed, label }) => (
  <div className="flex items-center gap-2">
    {passed ? (
      <Check className="w-4 h-4 text-green-500" />
    ) : (
      <X className="w-4 h-4 text-[#1A3A4A]/30" />
    )}
    <span className={`text-xs font-body ${passed ? 'text-green-600' : 'text-[#1A3A4A]/40'}`}>
      {label}
    </span>
  </div>
);

export default PortalLogin;
